import React, { useEffect, useState, useMemo } from 'react';
import './Dashboard.css';
import '../styles/animations.css';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalBots: 0,
    activeBots: 0,
    connections: 0,
    templates: 0
  });

  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({
    botId: 'all',
    dateFrom: '',
    dateTo: '',
    searchTerm: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [bots, setBots] = useState([]);

  useEffect(() => {
    // Parse stored data from localStorage
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
    const userBots = userInfo.bots || [];
    const connections = userInfo.connections || [];
    const templates = [];

    console.log('User Info:', userInfo);
    console.log('Bots:', userBots);

    setBots(userBots);

    // Extract all activities from all bots
    const allActivities = [];
    userBots.forEach(bot => {
      if (bot.activities && Array.isArray(bot.activities)) {
        bot.activities.forEach(activity => {
          allActivities.push({
            ...activity,
            bot_name: bot.name || `Bot ${bot.id}`,
            bot_id: bot.id
          });
        });
      }
    });

    // Sort activities by date (newest first)
    allActivities.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setActivities(allActivities);

    // Calculate active bots
    const activeBots = userBots.filter(bot => bot.active === true || bot.status === 'active').length;

    setDashboardData({
      totalBots: userBots.length,
      activeBots: activeBots,
      connections: connections.length,
      templates: templates.length
    });
  }, []);

  // Filter activities based on current filters
  const filteredActivities = useMemo(() => {
    return activities.filter(activity => {
      // Filter by bot
      if (filters.botId !== 'all' && activity.bot_id !== parseInt(filters.botId)) {
        return false;
      }

      // Filter by date range
      if (filters.dateFrom) {
        const activityDate = new Date(activity.created_at);
        const fromDate = new Date(filters.dateFrom);
        if (activityDate < fromDate) return false;
      }

      if (filters.dateTo) {
        const activityDate = new Date(activity.created_at);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59); // Include the entire day
        if (activityDate > toDate) return false;
      }

      // Filter by search term
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        return (
          activity.actividad.toLowerCase().includes(searchLower) ||
          activity.bot_name.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [activities, filters]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentActivities = filteredActivities.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for pagination controls
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination for many pages
      if (currentPage <= 3) {
        // Show first pages
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show last pages
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show middle pages
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (diffDays === 1) {
      return 'Ayer';
    } else if (diffDays < 7) {
      return `Hace ${diffDays} días`;
    } else {
      return date.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      botId: 'all',
      dateFrom: '',
      dateTo: '',
      searchTerm: ''
    });
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">Dashboard</h1>
      
      <div className="dashboard-cards card-grid">
        <div className="card card-animate">
          <h3>Total de Bots</h3>
          <p className="stat pulse">{dashboardData.totalBots}</p>
        </div>
        <div className="card card-animate">
          <h3>Bots Activos</h3>
          <p className="stat pulse">{dashboardData.activeBots}</p>
        </div>
        <div className="card card-animate">
          <h3>Conexiones</h3>
          <p className="stat pulse">{dashboardData.connections}</p>
        </div>
        <div className="card card-animate">
          <h3>Actividades</h3>
          <p className="stat pulse">{activities.length}</p>
        </div>
      </div>
      
      <div className="activity-feed card-animate">
        <div className="activity-header">
          <h2>Actividad Reciente</h2>
          <div className="activity-filters">
            <div className="filter-row">
              <select 
                value={filters.botId} 
                onChange={(e) => handleFilterChange('botId', e.target.value)}
                className="filter-select"
              >
                <option value="all">Todos los Bots</option>
                {bots.map(bot => (
                  <option key={bot.id} value={bot.id}>
                    {bot.name || `Bot ${bot.id}`}
                  </option>
                ))}
              </select>

              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                placeholder="Desde"
                className="filter-date"
              />

              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                placeholder="Hasta"
                className="filter-date"
              />

              <input
                type="text"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                placeholder="Buscar actividad..."
                className="filter-search"
              />

              <button onClick={clearFilters} className="clear-filters-btn">
                Limpiar
              </button>
            </div>
            
            {/* Items per page selector */}
            <div className="pagination-controls-top">
              <label htmlFor="itemsPerPage">Mostrar:</label>
              <select 
                id="itemsPerPage"
                value={itemsPerPage} 
                onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                className="items-per-page-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>elementos por página</span>
            </div>
          </div>
        </div>

        <div className="activity-list">
          {currentActivities.length > 0 ? (
            currentActivities.map((activity, index) => (
              <div key={startIndex + index} className="activity-item card-animate">
                <span className="status-indicator active"></span>
                <div className="activity-content">
                  <div className="activity-main">
                    <span className="time">{formatDate(activity.created_at)}</span>
                    <span className="description" style={{textAlign: "left"}}>{activity.actividad}</span>
                  </div>
                  <div className="activity-meta">
                    <span className="bot-name">Bot: {activity.bot_name}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-activities">
              <p>No se encontraron actividades con los filtros aplicados.</p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {filteredActivities.length > 0 && totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Mostrando {startIndex + 1} - {Math.min(endIndex, filteredActivities.length)} de {filteredActivities.length} actividades
              </span>
            </div>
            
            <div className="pagination-controls">
              <button 
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="pagination-btn pagination-btn-prev"
                title="Página anterior"
              >
                ‹
              </button>
              
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                )
              ))}
              
              <button 
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="pagination-btn pagination-btn-next"
                title="Página siguiente"
              >
                ›
              </button>
            </div>
            
            <div className="pagination-jump">
              <span>Ir a página:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page && page >= 1 && page <= totalPages) {
                    goToPage(page);
                  }
                }}
                className="pagination-jump-input"
              />
              <span>de {totalPages}</span>
            </div>
          </div>
        )}

        {/* Summary when no pagination needed */}
        {filteredActivities.length > 0 && totalPages <= 1 && (
          <div className="activity-summary">
            <p>Mostrando {filteredActivities.length} de {activities.length} actividades</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;